import React, { FC, useMemo } from 'react'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb, BreadCrumbItem } from 'src/Components/Breadcrumb'
import { APP } from 'src/Configs/App'
import { PublicRoutesEnum } from 'src/Router/PublicComponentsNewUI'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'

interface CreateCampaignBreadcrumbsNoAuthProps {
  platform?: CampaignPlatformTypeEnum
}

const PREVIOUS_ITEM_OFFSET = 2

const getBreadcrumbItems = (
  pathname: string,
  platform: CampaignPlatformTypeEnum | undefined,
  search: URLSearchParams,
): BreadCrumbItem[] => {
  const isSpotifyFlow = pathname.startsWith(
    PublicRoutesEnum.CreateSpotifyCampaign,
  )
  const isPreReleaseFlow = pathname.startsWith(
    PublicRoutesEnum.CreatePreReleaseTrackDetails,
  )

  const items: BreadCrumbItem[] = [
    {
      title: 'Campaign type',
      path: `${PublicRoutesEnum.CreateCampaign}?${search.toString()}`,
    },
  ]

  if (APP.PRE_RELEASE_CAMPAIGNS_ENABLED && isSpotifyFlow) {
    items.push({
      title: 'Release type',
      path: `${PublicRoutesEnum.CreateSpotifyCampaignReleaseType}?${search.toString()}`,
    })
  }

  if (isPreReleaseFlow) {
    items.push({
      title: 'Track details',
      path: `${PublicRoutesEnum.CreatePreReleaseTrackDetails}?${search.toString()}`,
    })
  } else {
    items.push({
      title: 'Track search',
      path:
        platform === CampaignPlatformTypeEnum.SPOTIFY
          ? `${PublicRoutesEnum.CreateSpotifyCampaign}?${search.toString()}`
          : `${PublicRoutesEnum.CreateTikTokCampaign}?${search.toString()}`,
    })
  }

  items.push({
    title: 'Contact details',
    path: '',
  })

  return items
}

export const CreateCampaignBreadcrumbsNoAuth: FC<
  CreateCampaignBreadcrumbsNoAuthProps
> = ({ platform }) => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()
  const { pathname } = useLocation()
  const { trackId } = useParams()

  const breadcrumbItems = useMemo(
    () => getBreadcrumbItems(pathname, platform, search),
    [pathname, platform, search],
  )

  const filteredBreadcrumbItems: BreadCrumbItem[] = breadcrumbItems.reduce<{
    items: BreadCrumbItem[]
    found: boolean
  }>(
    (acc, item) => {
      if (acc.found) {
        return acc
      }
      acc.items.push(item)
      const [itemPathname] = item.path.split('?')
      if (itemPathname === pathname) {
        acc.found = true
      }
      return acc
    },
    { items: [], found: false },
  ).items

  const onBreadcrumbButtonClick = (): void => {
    if (trackId) {
      navigate(
        `${pathname.replace(PublicRoutesEnum.SignUpArtist, '')}?${search.toString()}`,
      )
      return
    }
    const previousItem =
      filteredBreadcrumbItems[
        filteredBreadcrumbItems.length - PREVIOUS_ITEM_OFFSET
      ]
    navigate(
      previousItem?.path ??
        `${PublicRoutesEnum.CreateCampaign}?${search.toString()}`,
    )
  }

  return (
    <Breadcrumb
      items={filteredBreadcrumbItems}
      onButtonClick={onBreadcrumbButtonClick}
    />
  )
}
